import React, { useState, useRef, useCallback, useEffect, KeyboardEvent } from "react";
import { X } from "lucide-react";
import AssetGrid from "./AssetGrid";

interface TemplatesTabProps {
  assets: BrandAsset[];
  projectId: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: (assetId: string) => void;
  onUpdate: (assetId: string, updates: FormData) => void;
}

const PLATFORM_PRESETS = [
  "Meta", "Performance Max", "Demand Gen", "Display",
  "YouTube", "Pinterest", "LinkedIn", "TikTok", "Snapchat", "DV360",
];

const RATIO_PRESETS = ["1:1", "4:5", "9:16", "16:9", "1.91:1", "2:3", "1:2"];

const ChipSelect: React.FC<{
  label: string;
  values: string[];
  presets: string[];
  onChange: (values: string[]) => void;
}> = ({ label, values, presets, onChange }) => {
  const [input, setInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const available = presets.filter((p) => !values.includes(p));
  const filtered = input
    ? available.filter((p) => p.toLowerCase().includes(input.toLowerCase()))
    : available;

  const addValue = (val: string) => {
    const trimmed = val.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput("");
  };

  const removeValue = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      addValue(input);
    }
  };

  return (
    <div className="space-y-1">
      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <div className="flex flex-wrap gap-1">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-0.5 text-[10px] bg-blue-500/20 text-blue-300 rounded px-1.5 py-0.5">
            {v}
            <button onClick={() => removeValue(v)} className="hover:text-blue-100">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={values.length === 0 ? `Add ${label.toLowerCase()}...` : "Add..."}
          className="w-full bg-gray-800 border border-gray-600 text-gray-200 rounded px-2 py-1 text-[11px] focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
        {showDropdown && filtered.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-gray-800 border border-gray-600 rounded shadow-lg max-h-32 overflow-y-auto">
            {filtered.map((p) => (
              <button
                key={p}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { addValue(p); inputRef.current?.focus(); }}
                className="block w-full text-left px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-700"
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TemplatesTab: React.FC<TemplatesTabProps> = ({ assets, projectId, onUpload, onDelete, onUpdate }) => {
  const templateAssets = assets.filter((a) => a.type === "template");

  // Optimistic local overrides — keyed by assetId, stores pending metadata
  const [localMeta, setLocalMeta] = useState<Record<string, Record<string, any>>>({});
  const localMetaRef = useRef(localMeta);
  localMetaRef.current = localMeta;  // Keep ref in sync for debounce reads
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Clean up pending debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const handleMetaChange = useCallback((asset: BrandAsset, field: "platforms" | "aspect_ratios" | "description", values: string[] | string) => {
    // Optimistic update — instant UI
    setLocalMeta((prev) => {
      const baseMeta = prev[asset.id] ?? asset.metadata ?? {};
      const newMeta = { ...baseMeta, [field]: values };
      return { ...prev, [asset.id]: newMeta };
    });

    // Debounce the API call (800ms after last change)
    if (debounceTimers.current[asset.id]) clearTimeout(debounceTimers.current[asset.id]);
    debounceTimers.current[asset.id] = setTimeout(() => {
      const latestMeta = localMetaRef.current[asset.id];
      if (latestMeta) {
        const fd = new FormData();
        fd.append("metadata", JSON.stringify(latestMeta));
        onUpdate(asset.id, fd);
      }
      delete debounceTimers.current[asset.id];
    }, 800);
  }, [onUpdate]);

  const renderMeta = (asset: BrandAsset) => {
    const meta = localMeta[asset.id] ?? asset.metadata ?? {};
    const platforms: string[] = meta.platforms || [];
    const ratios: string[] = meta.aspect_ratios || [];

    return (
      <div className="space-y-2 mt-1">
        <ChipSelect
          label="Platforms"
          values={platforms}
          presets={PLATFORM_PRESETS}
          onChange={(vals) => handleMetaChange(asset, "platforms", vals)}
        />
        <ChipSelect
          label="Aspect ratios"
          values={ratios}
          presets={RATIO_PRESETS}
          onChange={(vals) => handleMetaChange(asset, "aspect_ratios", vals)}
        />
        <textarea
          value={meta.description || meta.layout_description || ""}
          onChange={(e) => handleMetaChange(asset, "description", e.target.value)}
          placeholder="Instructions for this template: what to preserve, where to place elements, style notes..."
          rows={2}
          className="w-full bg-gray-800 border border-gray-600 text-gray-200 rounded px-2 py-1 text-[11px] focus:ring-1 focus:ring-blue-500 focus:outline-none resize-y"
        />
      </div>
    );
  };

  return (
    <AssetGrid
      assets={templateAssets}
      assetType="template"
      onUpload={onUpload}
      onDelete={onDelete}
      onUpdate={onUpdate}
      renderMeta={renderMeta}
      emptyMessage="No templates yet. Upload layout composition references."
    />
  );
};

export default TemplatesTab;
